import { GREY_900 } from '@propertypal/shared/src/styles/theme';
import { LineData, LineValue, getXAxisLabels, getYAxisLabels } from '@propertypal/shared/src/utils/property/chartUtils';
import React, { FunctionComponent, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { Row } from '../layout';
import { Text } from '../typography';
import { Container, ChartWrapper, LabelIcon, LabelsRow, ViewBubble, NoDataKey } from './LineChart.style';

interface ViewBubbleType {
  x: number;
  y: number;
  title: string;
  subTitle: string;
}

interface Props {
  dottedPoints?: boolean;
  width: number;
  height: number;
  lines: LineData[];
  labelFormatter?: (input: number) => string;
  bubbleTextFormatter?: (input: number) => string;
  showLabels?: boolean;
  boldAxis?: boolean;
  hideYAxisLine?: boolean;
  bubbleAutoHeight?: boolean;
  lineTestID?: string;
  noDataLegend?: boolean;
  noDataText?: string;
}

const OFFSET_TOP = 10;
const Y_AXIS_INDENT = 40;
const X_AXIS_HEIGHT = 40;
const OFFSET_RIGHT = 10;

const LineChart: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const CHART_HEIGHT = props.height - X_AXIS_HEIGHT - OFFSET_TOP;
  const CHART_WIDTH = props.width - Y_AXIS_INDENT - OFFSET_RIGHT;
  const xAxisLabels = getXAxisLabels(props.lines);
  const yAxisLabels = getYAxisLabels(props.lines, CHART_HEIGHT);
  const columnWidth = CHART_WIDTH / (xAxisLabels.length - 1);
  const [viewBubble, setViewBubble] = useState<ViewBubbleType | null>(null);

  const getX = (point: number) => {
    return Y_AXIS_INDENT + columnWidth * point;
  };

  const getY = (value: number) => {
    return CHART_HEIGHT - (value / yAxisLabels[0]) * CHART_HEIGHT + OFFSET_TOP;
  };

  const hasNoDataKey = useMemo(() => {
    if (props.noDataLegend)
      return props.lines.some((line) => line.data.some((row, index) => row.hidden && index < line.data.length - 1));
    return false;
  }, [props.noDataLegend, JSON.stringify(props.lines)]);

  const handleMouseOverPoint = (x: number, y: number, lineValue: LineValue, line: LineData, index: number) => {
    const bubbleX = () => {
      if (index === 0) return x - 40;
      if (index === 1) return x - 80;
      if (index + 1 === line.data.length) return x - 120;
      if (index + 2 >= line.data.length) return x - 135;
      return x - 79;
    };

    setViewBubble({
      x: bubbleX(),
      y: y - 60,
      title: props.bubbleTextFormatter ? props.bubbleTextFormatter(lineValue.value) : String(lineValue.value),
      subTitle: `${line.key} (${lineValue.key})`,
    });
  };

  return (
    <Container style={{ width: props.width }}>
      {props.showLabels && (
        <LabelsRow>
          {props.lines.map((line) => (
            <Row style={{ alignItems: 'center', marginBottom: 10 }} key={line.key}>
              <LabelIcon color={line.color} />
              <Text fontSize={14}>{line.key}</Text>
            </Row>
          ))}
        </LabelsRow>
      )}

      <ChartWrapper onMouseLeave={() => setViewBubble(null)}>
        {viewBubble && (
          <ViewBubble
            data-testid="viewBubble"
            style={{ left: viewBubble.x, top: viewBubble.y }}
            autoHeight={props.bubbleAutoHeight}
          >
            <Text color={theme.white} align="center" data-testid="viewBubbleTitle">
              {viewBubble.title}
            </Text>
            <Text color={GREY_900} align="center" fontSize={12}>
              {viewBubble.subTitle}
            </Text>
          </ViewBubble>
        )}

        <svg width={props.width} height={props.height + 10} style={{ overflow: 'visible' }}>
          {/* Y AXIS */}
          {yAxisLabels.map((axis) => {
            const y = getY(axis);
            const x = getX(0);

            return (
              <g key={String(y)}>
                <text
                  key={axis}
                  x={x - 5}
                  y={y + 3}
                  fontSize={12}
                  fill={theme.textLight}
                  textAnchor="end"
                  data-testid={`yAxis${axis}Label`}
                  fontWeight={props.boldAxis ? 'bold' : undefined}
                >
                  {props.labelFormatter ? props.labelFormatter(axis) : axis}
                </text>
              </g>
            );
          })}

          <line
            x1={getX(0)}
            y1={getY(0)}
            x2={props.width - OFFSET_RIGHT}
            y2={getY(0)}
            stroke={theme.backgroundLight}
            strokeWidth="1px"
          />

          {!props.hideYAxisLine && (
            <line
              x1={getX(0)}
              y1={getY(0)}
              x2={getX(0)}
              y2={getY(yAxisLabels[0])}
              stroke={theme.backgroundLight}
              strokeWidth="1"
              data-testid="yAxisLine"
            />
          )}

          {props.lines.map((line) => (
            <g key={line.key} data-testid={`${props.lineTestID ? `${props.lineTestID}-` : ''}linechart-line`}>
              {line.data.map((row, index) => {
                const next = line.data[index + 1];
                const x1 = getX(index);
                const y1 = getY(row.value);
                const x2 = getX(index + 1);
                const y2 = getY(next?.value != null ? next.value : row.value);

                if (row.empty) {
                  return null;
                }

                // Showing the last item when its hidden exceeds the width of chart
                // https://app.shortcut.com/propertypal/story/27492/pdp-price-history#activity-29266
                if (row.hidden && index < line.data.length - 1) {
                  const xx = x1 + columnWidth / 2;
                  const yy = OFFSET_TOP + CHART_HEIGHT * 0.64;
                  return (
                    <React.Fragment key={String(x1)}>
                      <rect
                        x={x1}
                        y={OFFSET_TOP}
                        width={columnWidth}
                        height={CHART_HEIGHT}
                        fill="rgba(0,0,0,0.04)"
                        pointerEvents="none"
                      />

                      {!props.noDataLegend && (
                        <text fontSize={14} x={xx} y={yy} fill={theme.textDark} transform={`rotate(-90, ${xx}, ${yy})`}>
                          {props.noDataText || 'No data available'}
                        </text>
                      )}
                    </React.Fragment>
                  );
                }

                return (
                  <g key={String(x1)}>
                    {index < line.data.length - 1 && next && !next.empty && (
                      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={line.color} strokeWidth="2" />
                    )}

                    {row.highlight && index < line.data.length - 1 && (
                      <rect
                        x={x1}
                        y={OFFSET_TOP}
                        width={columnWidth}
                        height={CHART_HEIGHT}
                        fill={line.color}
                        fillOpacity={0.1}
                      />
                    )}

                    {props.dottedPoints && !row.hidden && <circle cx={x1} cy={y1} r="4" fill={line.color} />}

                    <rect
                      key={String(x1)}
                      x={x1 - 10}
                      y={y1 - 10}
                      width={20}
                      height={20}
                      fill="transparent"
                      onMouseOver={() => handleMouseOverPoint(x1, y1, row, line, index)}
                      data-testid={`point-${x1}-${y1}`}
                    />
                  </g>
                );
              })}
            </g>
          ))}

          {/* X AXIS */}
          {xAxisLabels.map((label, index) => {
            const x = getX(index) + 5;
            const y = CHART_HEIGHT + 25;

            if (label == null) return null;

            return (
              <g key={label}>
                <text
                  fontSize={12}
                  fill={theme.textLight}
                  textAnchor="end"
                  x={x}
                  y={y}
                  transform={`rotate(${props.width < 400 ? -45 : -35}, ${x}, ${y})`}
                  fontWeight={props.boldAxis ? 'bold' : undefined}
                >
                  {label}
                </text>
              </g>
            );
          })}
        </svg>
      </ChartWrapper>

      {hasNoDataKey && (
        <Row mt={15} justifyContent="center">
          <NoDataKey />

          <Text fontSize={12}>{props.noDataText || 'No data available'}</Text>
        </Row>
      )}
    </Container>
  );
};

export default LineChart;
